import {
  ContentIcon,
  DraftShowcase,
  MathShowcase,
  SearchShowcase,
} from "@dropbox/dash-component-library";
import { Avatar } from "@dropbox/dig-components/avatar";
import { Chip } from "@dropbox/dig-components/chip";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { Box, ThemeContainer, ThemeProvider } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { LinkLine, StacksLine } from "@dropbox/dig-icons/assets";
import { DashStackIconWithStack } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { ShortcutTitle } from "@mirage/search/SearchResults/ShortcutTitle";
import { getTimeAgoStringFromTimestamp } from "@mirage/search/SearchResults/util/resultUtil";
import { tagged } from "@mirage/service-logging";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import {
  tokenizeTitle,
  toQueryParts,
} from "@mirage/service-typeahead-search/service/utils/url-shortcut-utils";
import { getInitials } from "@mirage/shared/account/user_i18n";
import {
  DesktopFileIcon,
  getExtension,
  URLShortcutIcon,
  UrlSrcIcon,
} from "@mirage/shared/icons";
import DesktopApplicationIcon from "@mirage/shared/icons/DesktopApplicationIcon";
import { isRecordTypeTagEvent } from "@mirage/shared/search/search-result";
import { faviconSrcForSrcUrl } from "@mirage/shared/util/favicon";
import { getAbbrDate } from "@mirage/shared/util/time";
import { getTheme } from "@mirage/stacks/themes/utils";
import i18n from "@mirage/translations";
import {
  AttributeValueText,
  AvatarDisplayNameMetadata,
  BASE_PREVIEW_ICON_CONTENT_SIZE,
  BASE_PREVIEW_ICON_SIZE,
  FacePileAttribute,
  getCalendarEventInfo,
  getChatPreview,
  getContentInfo,
  PreviewIconContainer,
  SIDE_PANEL_TOOLTIP_PROPS,
} from "./previews";

import type { stacks } from "@dropbox/api-v2-client";
import type { ContentInfoPanelProps } from "@dropbox/dash-component-library";
import type { TypeaheadResult } from "@mirage/mosaics/SearchBarWithTypeahead/useConvertToTypeaheadResults";

const logger = tagged("get-typeahead-result-info");

export const getTypeaheadResultInfo = (
  result: TypeaheadResult,
  query: string,
  isDarkMode: boolean,
): ContentInfoPanelProps => {
  const { type, scoredResult } = result;

  let info: Partial<ContentInfoPanelProps> = {};
  switch (scoredResult.type) {
    case typeahead.ResultType.Stack: {
      const stack = scoredResult.result;
      const {
        stack_data,
        sharing_data,
        last_modified_time_utc_sec,
        created_time_utc_ms,
      } = stack;
      const { description, color_index, custom_color } = stack_data || {};
      const { members } = sharing_data || {};

      const attributes = [];
      if (members && members.length > 0) {
        const users = members
          .map((member) => member.subject)
          .filter(
            (subject) => subject && subject[".tag"] === "user",
          ) as stacks.StackSharingSubjectUser[];

        if (users.length > 0) {
          attributes.push({
            label: i18n.t("members_label"),
            value: (
              <FacePileAttribute
                users={users}
                countText={i18n.t("num_members", { count: users.length })}
              />
            ),
          });
        }
      }
      if (last_modified_time_utc_sec) {
        attributes.push({
          label: i18n.t("last_updated_label"),
          value: (
            <AttributeValueText
              value={getTimeAgoStringFromTimestamp(
                last_modified_time_utc_sec * 1000,
              )}
            />
          ),
        });
      }

      if (created_time_utc_ms) {
        attributes.push({
          label: i18n.t("created_label"),
          value: (
            <AttributeValueText value={getAbbrDate(created_time_utc_ms)} />
          ),
        });
      }

      info = {
        subtitle: description ? (
          <Text color="subtle">
            <Truncate lines={2} tooltipControlProps={SIDE_PANEL_TOOLTIP_PROPS}>
              {description}
            </Truncate>
          </Text>
        ) : undefined,
        contentPreview: (
          <ThemeProvider overrides={getTheme(color_index, custom_color).subtle}>
            <ThemeContainer>
              <PreviewIconContainer
                icon={<DashStackIconWithStack size="xlarge" stack={stack} />}
              />
            </ThemeContainer>
          </ThemeProvider>
        ),
        attributes,
      };
      break;
    }
    case typeahead.ResultType.StackItem: {
      const stackItem = scoredResult.result;
      const { stackIds, custom_name, name, lastModifiedTime, metadata_title } =
        stackItem;

      const attributes = [];
      if (stackItem.creator && stackItem.creator.displayName) {
        attributes.push({
          label: i18n.t("updated_by_label"),
          value: <AvatarDisplayNameMetadata user={stackItem.creator} />,
        });
      }

      if (stackIds.length > 0) {
        attributes.push({
          label: i18n.t("found_in_label"),
          value: (
            <Chip variant="standard" size="small">
              <Chip.IconAccessory>
                <UIIcon src={StacksLine} />
              </Chip.IconAccessory>
              <Chip.Content>
                <Text size="small">
                  <Truncate
                    maxWidth={200}
                    tooltipControlProps={SIDE_PANEL_TOOLTIP_PROPS}
                  >
                    {stackIds.length === 1 && stackIds[0].name
                      ? stackIds[0].name
                      : `${stackIds.length} ${i18n.t(
                          "stack_item_typeahead_subtitle_badge",
                        )}`}
                  </Truncate>
                </Text>
              </Chip.Content>
            </Chip>
          ),
        });
      }

      info = {
        contentPreview: (
          <PreviewIconContainer
            icon={
              <ContentIcon
                icon={
                  stackItem.url ? (
                    <UrlSrcIcon
                      name={i18n.t("icon_for_item")}
                      size={BASE_PREVIEW_ICON_CONTENT_SIZE}
                      iconUrl={faviconSrcForSrcUrl(
                        stackItem.url,
                        BASE_PREVIEW_ICON_CONTENT_SIZE,
                        isDarkMode,
                      )}
                    />
                  ) : (
                    <UIIcon src={LinkLine} />
                  )
                }
                hasBackground={true}
                shouldConstrainContent={false}
                size="3xlarge"
              />
            }
          />
        ),
        title: [custom_name, name, metadata_title].find(Boolean),
        subtitle: lastModifiedTime
          ? getTimeAgoStringFromTimestamp(lastModifiedTime * 1000)
          : undefined,
        attributes,
      };
      break;
    }
    case typeahead.ResultType.CalendarEvent: {
      info = { ...info, ...getCalendarEventInfo(scoredResult.result) };
      break;
    }
    case typeahead.ResultType.SearchResult: {
      const searchResult = scoredResult.result;
      info = {
        ...info,
        ...(isRecordTypeTagEvent(searchResult)
          ? getCalendarEventInfo(searchResult)
          : getContentInfo(searchResult)),
      };
      break;
    }
    case typeahead.ResultType.Recommendation:
      info = { ...info, ...getContentInfo(scoredResult.result) };
      break;
    case typeahead.ResultType.DesktopFile: {
      const { providerUpdateAtMs, description } = scoredResult.result;
      info = {
        ...info,
        subtitle: (
          <Text color="subtle">
            <Truncate
              location={0}
              tooltipControlProps={SIDE_PANEL_TOOLTIP_PROPS}
            >
              {description}
            </Truncate>
          </Text>
        ),
        contentPreview: (
          <Box
            borderWidth="1"
            borderStyle="Solid"
            borderColor="Border Subtle"
            borderRadius="Small"
            backgroundColor="Background Base"
          >
            <DesktopFileIcon
              result={scoredResult.result}
              size={BASE_PREVIEW_ICON_SIZE}
            />
          </Box>
        ),
        attributes: [
          {
            label: i18n.t("last_updated_label"),
            value: (
              <AttributeValueText
                value={getTimeAgoStringFromTimestamp(providerUpdateAtMs)}
              />
            ),
          },
          {
            label: i18n.t("file_type_label"),
            value: (
              <AttributeValueText
                value={getExtension(scoredResult.result)?.toUpperCase()}
              />
            ),
          },
        ],
      };
      break;
    }
    case typeahead.ResultType.DesktopApplication: {
      info = {
        ...info,
        contentPreview: (
          <DesktopApplicationIcon
            result={scoredResult.result}
            size={BASE_PREVIEW_ICON_SIZE}
          />
        ),
      };
      break;
    }
    case typeahead.ResultType.SearchFilter:
    case typeahead.ResultType.PreviousQuery:
    case typeahead.ResultType.SuggestedQuery: {
      info = {
        contentPreview: <SearchShowcase />,
        previewSubtitle: i18n.t("search_preview_tagline"),
        align: "center",
        title: undefined,
        subtitle: undefined,
      };
      break;
    }
    case typeahead.ResultType.URLShortcut: {
      const { titleTemplate, parameters } = scoredResult.result;
      const { activeHotword, url } = parameters;
      const queryParts = toQueryParts(query);

      info = {
        title: (
          <ShortcutTitle
            title={tokenizeTitle(titleTemplate, activeHotword, queryParts)}
          />
        ),
        subtitle: <span style={{ wordWrap: "break-word" }}>{url}</span>,
        contentPreview: (
          <ContentIcon
            size="xlarge"
            hasBackground={true}
            icon={<URLShortcutIcon result={scoredResult.result} size={24} />}
          />
        ),
      };
      break;
    }
    case typeahead.ResultType.MathCalculation: {
      const { query, answer } = scoredResult.result;
      info = {
        align: "top",
        contentPreview: (
          <Box style={{ width: "80%" }}>
            <MathShowcase equation={query} solution={answer} />
          </Box>
        ),
        title: undefined,
        subtitle: undefined,
      };
      break;
    }

    case typeahead.ResultType.AskSuggestion:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.text,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.AskUserQuery:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.query,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.RecentChat:
      // TODO: Finalize copy for subtitle
      info = getChatPreview(
        scoredResult.result.title,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.OrganizeUserQuery:
      // TODO: Add preview related to stacks
      break;
    case typeahead.ResultType.DraftWithSource:
    case typeahead.ResultType.DraftFromTemplate:
    case typeahead.ResultType.DraftFromTone:
      info = {
        title: undefined,
        subtitle: undefined,
        contentPreview: (
          <Box width="100%" paddingX="44">
            <DraftShowcase rows={3} animate={true} />
          </Box>
        ),
        previewSubtitle: i18n.t("draft_preview_tagline"),
        align: "center",
      };
      break;
    case typeahead.ResultType.WriteUserQuery:
      // TODO: Add additional context and UI for draft
      info = getChatPreview(
        scoredResult.result.query,
        i18n.t("answers_preview_tagline"),
      );
      break;
    case typeahead.ResultType.MyProfileSuggestion:
    case typeahead.ResultType.PeopleFilter:
    case typeahead.ResultType.PeopleRecommendation: {
      const person = scoredResult.result;

      const attributes = [
        {
          label: i18n.t("email_label"),
          value: <AttributeValueText value={person.email} />,
        },
      ];

      info = {
        title: person.displayName,
        subtitle: person.role,
        contentPreview: (
          <Avatar
            src={person.profilePhotoUrl}
            alt={person.displayName}
            size="large"
            hasNoOutline
            style={{ height: "72px", width: "72px" }}
          >
            {getInitials(person.displayName || person.email || "")}
          </Avatar>
        ),
        attributes,
      };
      break;
    }

    case typeahead.ResultType.Skeleton:
    case typeahead.ResultType.LoadingCompletion: {
      break;
    }

    default:
      // TODO: Add a default side panel display for non-specified result types
      scoredResult satisfies never;
      logger.warn(`Invalid result type in getTypeaheadResultInfo: ${type}`);
      break;
  }

  return {
    title: result.title,
    subtitle: result.subtitle,
    ...info,
  };
};
